import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {WorksDto, WorksModel} from "src/app/models/WorksModel";
import {isBook} from "src/app/utils/works-type-guards.util";
import {User} from "src/app/modules/auth/models/User";
import {Subscription} from "rxjs";
import {ModalService} from "../../services/modal.service";
import {WorksService} from "../../../shared/services/works.service";
import {CustomError} from "../../../shared/interfaces/CustomError.interface";
import {UserService} from "src/app/modules/shared/services/user.service";
import {ToastService} from "../../services/toast.service";
import {AuthService} from "src/app/modules/auth/services/auth.service";
import {isEqual, omit} from "lodash-es";
import {CacheService} from "../../../shared/services/cache.service";
import {ListsService} from "../../../shared/services/list.service";
import {TranslateService, TranslateModule} from "@ngx-translate/core";
import {List} from "src/app/models/List";
import {FormsModule} from "@angular/forms";
import {NgClass} from "@angular/common";
import {RealtimeInvalidationService} from "src/app/modules/shared/services/realtime-invalidation.service";
import {UserLocaleService} from "src/app/services/user-locale.service";
import {
  allowsCrossRegionTranslationFallback,
  resolveTranslation,
} from "src/app/utils/translation.util";
import {WorkBulkActionsService} from "../../../shared/services/work-bulk-actions.service";

@Component({
  selector: "app-add-to-list",
  templateUrl: "./add-to-list.component.html",
  styleUrls: ["./add-to-list.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, NgClass, TranslateModule],
})
export class AddToListComponent implements OnInit, OnDestroy {
  @Input() item!: WorksModel;
  @Input() items: WorksModel[] = [];
  @Input() user!: User;
  @Input() inModal?: boolean = false;
  @Output() creatingList = new EventEmitter<boolean>();
  @Output() closeModal = new EventEmitter<{
    event: Event;
    itemProviderId: string;
  }>();
  private readonly listsUpdatedCallback: () => void;
  searchTerm = "";
  public isLoading = true;
  public lists: List[] = [];
  public isBook = isBook;
  public isClearVisible = false;
  private hasSelectionContext = false;
  private additionText?: string;
  private readonly subscriptions: Subscription[] = [];
  private socketCleanup?: () => void;
  private readonly cacheRevalidateMs = 60_000;
  private readonly modalCloseDelayMs = 2_000;
  private modalCloseTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly translate: TranslateService,
    private readonly authService: AuthService,
    private readonly cacheService: CacheService,
    private readonly modalService: ModalService,
    private readonly worksService: WorksService,
    private readonly workBulkActionsService: WorkBulkActionsService,
    private readonly userService: UserService,
    private readonly listsService: ListsService,
    private readonly realtimeInvalidationService: RealtimeInvalidationService,
    private readonly toastService: ToastService,
    private readonly userLocale: UserLocaleService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.listsUpdatedCallback = () => {
      this.updateUserLists(true);
    };

    const translateSubscription = this.translate.onLangChange.subscribe(() => {
      this.setAdditionText("ADDED_TO");
      this.cdr.markForCheck();
    });
    this.subscriptions.push(translateSubscription);
  }

  ngOnInit() {
    this.setAdditionText("ADDED_TO");
    this.cdr.markForCheck();

    if (this.inModal) {
      const modalItemSubscription = this.modalService.modalItem$.subscribe({
        next: (modalData) => {
          const modalItem = modalData?.item as WorksModel;
          const isAddToListModal = modalData?.modalId?.startsWith("addList_");
          if (!modalItem || !isAddToListModal) {
            return;
          }
          if (this.item && modalItem.providerId !== this.item.providerId) {
            return;
          }
          const nextLists = modalItem.lists ?? this.item?.lists;
          const nextItem: WorksModel =
            nextLists !== undefined
              ? {
                  ...modalItem,
                  lists: [...nextLists],
                }
              : modalItem;
          this.item = nextItem;
          this.hasSelectionContext = modalData.items !== undefined;
          this.cdr.markForCheck();
        },
      });
      this.subscriptions.push(modalItemSubscription);
    }

    const userSubscription = this.userService.getUser(this.user._id).subscribe({
      next: (user: User) => {
        const userToCompare = omit(this.user, ["tokens"]);
        if (user && !isEqual(user, userToCompare)) {
          this.authService.user = user;
        }
        this.updateUserLists();
      },
    });

    this.socketCleanup = this.realtimeInvalidationService.subscribe(
      "listsUpdated",
      this.listsUpdatedCallback,
      {key: "lists:add-to-list"}
    );

    this.subscriptions.push(userSubscription);
  }

  public getItemTranslation(item: WorksModel, field: string): string {
    const locale = this.userLocale.getLocale();
    if (locale.split("-")[0] !== "en") {
      const translated = resolveTranslation(item.translations, field, locale, {
        crossRegionFallback: allowsCrossRegionTranslationFallback(field),
      });
      if (translated) {
        return translated;
      }
    }

    return item[field as keyof WorksModel] as string;
  }

  private setAdditionText(key: string): void {
    const translateSubscription = this.translate
      .get(key)
      .subscribe((translatedText) => {
        this.additionText = translatedText;
        this.cdr.markForCheck();
      });
    this.subscriptions.push(translateSubscription);
  }

  get filteredLists() {
    return this.lists?.filter((list) =>
      list.name.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  private updateUserLists(forceRefresh = false) {
    if (forceRefresh) {
      this.fetchLists();
      this.cdr.markForCheck();
      return;
    }
    this.checkCacheFirst();
    this.cdr.markForCheck();
  }

  onSearchTermChange(): void {
    if (this.searchTerm) {
      this.isClearVisible = true;
    } else {
      this.isClearVisible = false;
    }
  }

  private checkCacheFirst(): void {
    const cacheKey = "lists";
    this.cacheService.getFromCache(cacheKey).then((cachedData) => {
      if (cachedData) {
        this.applyListsPreservingOrder(cachedData as List[]);
        this.isLoading = false;
        this.cdr.markForCheck();
        this.cacheService
          .shouldRevalidate(cacheKey, this.cacheRevalidateMs)
          .then((shouldRevalidate) => {
            if (shouldRevalidate) {
              this.fetchLists();
            }
          })
          .catch(() => {
            this.fetchLists();
          });
      } else {
        this.fetchLists();
      }
    });
  }

  fetchLists() {
    const listsSubscription = this.listsService
      .getLists(this.user._id, false)
      .subscribe({
        next: (lists: List[] | null) => {
          this.applyListsPreservingOrder(lists ?? []);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
    this.subscriptions.push(listsSubscription);
  }

  closeModalAddToList(event: Event) {
    this.closeModal.emit({event, itemProviderId: this.item.providerId});
  }

  addToList(event: Event, work: WorksModel, user: User, list: List) {
    if (this.isSelectionBatch) {
      this.addSelectionToList(event, user, list);
      return;
    }
    (event.target as HTMLElement).classList.add("loading");

    const worksDto: WorksDto = {
      workId: work._id,
    };

    const addWorkSubscription = this.worksService
      .addWorkByType(worksDto, work.type, user, list._id)
      .subscribe({
        next: (work: WorksModel | null) => {
          if (work) {
            const currentLists = this.item.lists ?? [];
            const alreadyInList = currentLists.some(
              (itemList) =>
                itemList.id === list._id || itemList._id === list._id
            );

            this.item = {
              ...this.item,
              lists: alreadyInList
                ? [...currentLists]
                : [
                    ...currentLists,
                    {name: list.name, id: list._id, slug: list.slug},
                  ],
            };

            (event.target as HTMLElement).classList.remove("loading");
            (event.target as HTMLElement).classList.add("success");

            if (this.inModal) {
              this.modalService.updateItem(this.item);
            }

            this.sendWorkAddedToast(work, list);
            this.cdr.markForCheck();
            if (!alreadyInList) {
              this.scheduleModalClose();
            }

            setTimeout(() => {
              (event.target as HTMLElement).classList.remove("success");
            }, 5000);
          }
        },
        error: (error: CustomError) => {
          (event.target as HTMLElement).classList.remove("loading");
          this.toastService.sendToast({
            text: error.error.error,
            classes: "bg-form-error",
          });
        },
      });
    this.subscriptions.push(addWorkSubscription);
  }

  openModal(modalId: string) {
    this.modalService.openItemModal(
      modalId,
      this.item,
      undefined,
      this.isSelectionBatch ? this.selectedItems : undefined
    );
    this.creatingList.emit(true);
  }

  clearSearch(): void {
    this.searchTerm = "";
    this.isClearVisible = false;
  }

  onKeyUp(
    event: KeyboardEvent,
    type: string,
    work?: WorksModel,
    user?: User,
    list?: List
  ): void {
    if (event.key === "Enter" || event.key === " ") {
      if (type === "closeModalAddToList") {
        this.closeModalAddToList(event);
      } else if (type === "clearSearch") {
        this.clearSearch();
      } else if (type === "addToList" && work && user && list) {
        this.addToList(event, work, user, list);
      } else if (type === "addList") {
        this.openModal("addList");
      }
    }
  }

  trackByFn(_index: number, item: List) {
    return item._id;
  }

  isInList(list: List): boolean {
    return this.selectedItems.every((item) => this.itemIsInList(item, list));
  }

  isPartiallyInList(list: List): boolean {
    const count = this.selectedItems.filter((item) =>
      this.itemIsInList(item, list)
    ).length;
    return count > 0 && count < this.selectedItems.length;
  }

  private itemIsInList(item: WorksModel, list: List): boolean {
    if (item.lists !== undefined) {
      return item.lists.some(
        (itemList) => itemList.id === list._id || itemList._id === list._id
      );
    }

    const listWorks = list.works ?? [];
    if (listWorks.length === 0) {
      return false;
    }

    const itemId = item._id;
    return listWorks.some((work) => itemId && work._id === itemId);
  }

  private addSelectionToList(event: Event, user: User, list: List): void {
    const button = event.currentTarget as HTMLElement;
    button.classList.add("loading");
    const references = this.selectedItems.map((item) => ({
      _id: item._id,
      type: item.type,
    }));
    const subscription = this.workBulkActionsService
      .addToList(user._id, list._id, references)
      .subscribe({
        next: (response) => {
          const addedCount = response.results.filter(
            (result) => result.status === "success"
          ).length;
          const completedKeys = new Set(
            response.results
              .filter(
                (result) =>
                  result.status === "success" ||
                  result.status === "alreadyInList"
              )
              .map((result) => `${result.work.type}:${result.work._id}`)
          );
          this.items = this.selectedItems.map((item) =>
            completedKeys.has(`${item.type}:${item._id}`)
              ? this.withList(item, list)
              : item
          );
          this.item = this.items[0] ?? this.item;
          button.classList.remove("loading");
          button.classList.add("success");
          this.modalService.bulkActionCompleted.emit({
            references: references.filter((reference) =>
              completedKeys.has(`${reference.type}:${reference._id}`)
            ),
            removesFromSource: false,
            updatedWorks: this.items,
          });
          const allCompleted = completedKeys.size === references.length;
          if (allCompleted && references.length === 1) {
            this.sendWorkAddedToast(this.items[0], list);
          } else {
            this.toastService.sendToast({
              text: allCompleted
                ? "WORK_SELECTION_ACTION_SUCCESS"
                : "WORK_SELECTION_ACTION_PARTIAL",
              classes: allCompleted
                ? "bg-form-success"
                : completedKeys.size > 0
                ? "bg-primary"
                : "bg-form-error",
            });
          }
          this.cdr.markForCheck();
          if (addedCount > 0) {
            this.scheduleModalClose();
          }
          setTimeout(() => button.classList.remove("success"), 5000);
        },
        error: (error: CustomError) => {
          button.classList.remove("loading");
          this.toastService.sendToast({
            text: error.error.error,
            classes: "bg-form-error",
          });
        },
      });
    this.subscriptions.push(subscription);
  }

  private sendWorkAddedToast(work: WorksModel, list: List): void {
    const title = this.getItemTranslation(work, "title");
    this.toastService.sendToast({
      text: `${title} ${this.additionText} ${list.name}`,
      translatable: false,
      classes: "bg-form-success",
    });
  }

  private withList(item: WorksModel, list: List): WorksModel {
    if (this.itemIsInList(item, list)) {
      return item;
    }
    return {
      ...item,
      lists: [
        ...(item.lists ?? []),
        {name: list.name, id: list._id, slug: list.slug},
      ],
    };
  }

  private get selectedItems(): WorksModel[] {
    return this.items.length > 0 ? this.items : [this.item];
  }

  public get selectedItemCount(): number {
    return this.selectedItems.length;
  }

  public get isSelectionBatch(): boolean {
    return this.hasSelectionContext || this.selectedItemCount > 1;
  }

  private scheduleModalClose(): void {
    if (!this.inModal) {
      return;
    }
    if (this.modalCloseTimeoutId) {
      clearTimeout(this.modalCloseTimeoutId);
    }

    const modalId = `addList_${this.item.providerId}`;
    this.modalCloseTimeoutId = setTimeout(() => {
      if (this.modalService.isModalOpen(modalId)) {
        this.modalService.closeModal(modalId);
      }
      this.modalCloseTimeoutId = null;
    }, this.modalCloseDelayMs);
  }

  private applyListsPreservingOrder(nextLists: List[]): void {
    if (this.lists.length === 0) {
      this.lists = [...nextLists];
      return;
    }

    const orderById = new Map<string, number>();
    this.lists.forEach((list, index) => {
      orderById.set(list._id, index);
    });

    const existingLists = nextLists
      .filter((list) => orderById.has(list._id))
      .sort((a, b) => orderById.get(a._id)! - orderById.get(b._id)!);
    const newLists = nextLists.filter((list) => !orderById.has(list._id));

    this.lists = [...existingLists, ...newLists];
  }

  ngOnDestroy(): void {
    if (this.modalCloseTimeoutId) {
      clearTimeout(this.modalCloseTimeoutId);
    }
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
    this.socketCleanup?.();
  }
}
